import { ApolloLink, InMemoryCache as InMemoryCache$1, ApolloProvider, Observable as Observable$1, ApolloClient as ApolloClient$1 } from '@apollo/client/index.js';
import { hasDirectives, Observable, removeDirectivesFromDocument, mergeIncrementalData, print } from '@apollo/client/utilities/index.js';
import { canonicalStringify } from '@apollo/client/cache/index.js';
import { invariant } from 'ts-invariant';
import React, { createContext, useRef, useContext, useSyncExternalStore } from 'react';

// src/AccumulateMultipartResponsesLink.ts
var AccumulateMultipartResponsesLink = class extends ApolloLink {
  maxDelay;
  constructor(config) {
    super();
    this.maxDelay = config.cutoffDelay;
  }
  request(operation, forward) {
    if (!forward) {
      throw new Error("This is not a terminal link!");
    }
    const operationContainsMultipartDirectives = hasDirectives(
      ["defer"],
      operation.query
    );
    const upstream = forward(operation);
    if (!operationContainsMultipartDirectives)
      return upstream;
    const maxDelay = this.maxDelay;
    let accumulatedData, maxDelayTimeout;
    return new Observable((subscriber) => {
      const upstreamSubscription = upstream.subscribe({
        next: (result) => {
          if (accumulatedData) {
            if (accumulatedData.data && "incremental" in result) {
              accumulatedData.data = mergeIncrementalData(
                accumulatedData.data,
                result
              );
            } else if (result.data) {
              accumulatedData.data = result.data;
            }
            if (result.errors) {
              accumulatedData.errors = [
                ...accumulatedData.errors || [],
                ...result.errors || []
              ];
            }
            if (result.extensions)
              accumulatedData.extensions = {
                ...accumulatedData.extensions,
                ...result.extensions
              };
          } else {
            accumulatedData = result;
          }
          if (!maxDelay) {
            flushAccumulatedData();
          } else if (!maxDelayTimeout) {
            maxDelayTimeout = setTimeout(flushAccumulatedData, maxDelay);
          }
        },
        error: (error) => {
          if (maxDelayTimeout)
            clearTimeout(maxDelayTimeout);
          subscriber.error(error);
        },
        complete: () => {
          if (maxDelayTimeout) {
            clearTimeout(maxDelayTimeout);
            flushAccumulatedData();
          }
          subscriber.complete();
        }
      });
      function flushAccumulatedData() {
        subscriber.next(accumulatedData);
        subscriber.complete();
        upstreamSubscription.unsubscribe();
      }
      return function cleanUp() {
        clearTimeout(maxDelayTimeout);
        upstreamSubscription.unsubscribe();
      };
    });
  }
};
function getDirectiveArgumentValue(directive, argument) {
  return directive.arguments?.find((arg) => arg.name.value === argument)?.value;
}
var RemoveMultipartDirectivesLink = class extends ApolloLink {
  stripDirectives = [];
  constructor(config) {
    super();
    if (config.stripDefer !== false)
      this.stripDirectives.push("defer");
  }
  request(operation, forward) {
    if (!forward) {
      throw new Error("This is not a terminal link!");
    }
    const { query } = operation;
    let modifiedQuery = query;
    modifiedQuery = removeDirectivesFromDocument(
      this.stripDirectives.map((directive) => ({
        test(node) {
          let shouldStrip = node.kind === "Directive" && node.name.value === directive;
          const label = getDirectiveArgumentValue(node, "label");
          if (label?.kind === "StringValue" && label.value.startsWith("SsrDontStrip")) {
            shouldStrip = false;
          }
          return shouldStrip;
        },
        remove: true
      })).concat({
        test(node) {
          if (node.kind !== "Directive")
            return false;
          const label = getDirectiveArgumentValue(node, "label");
          return label?.kind === "StringValue" && label.value.startsWith("SsrStrip");
        },
        remove: true
      }),
      modifiedQuery
    );
    if (modifiedQuery === null) {
      return Observable.of({});
    }
    operation.query = modifiedQuery;
    return forward(operation);
  }
};
var SSRMultipartLink = class extends ApolloLink {
  constructor(config = {}) {
    const combined = ApolloLink.from([
      new RemoveMultipartDirectivesLink({
        stripDefer: config.stripDefer
      }),
      new AccumulateMultipartResponsesLink({
        cutoffDelay: config.cutoffDelay || 0
      })
    ]);
    super(combined.request);
  }
};
var InMemoryCache = class extends InMemoryCache$1 {
};
var DataTransportContext = /* @__PURE__ */ createContext(null);

// src/DataTransportAbstraction/useTransportValue.tsx
function useTransportValue(value) {
  const dataTransport = useContext(DataTransportContext);
  if (!dataTransport)
    throw new Error(
      "useTransportValue must be used within a streaming-specific ApolloProvider"
    );
  const valueRef = dataTransport.useStaticValueRef(value);
  const retVal = useSyncExternalStore(
    () => () => {
    },
    () => value,
    () => valueRef.current
  );
  if (retVal === value) {
    valueRef.current = void 0;
  }
  return retVal;
}

// src/DataTransportAbstraction/hooks.ts
var hookWrappers = {
  useFragment(orig_useFragment) {
    return wrap(orig_useFragment, ["data", "complete", "missing"]);
  },
  useQuery(orig_useQuery) {
    return wrap(
      orig_useQuery,
      ["data", "loading", "networkStatus", "called"]
    );
  },
  useSuspenseQuery(orig_useSuspenseQuery) {
    return wrap(orig_useSuspenseQuery, ["data", "networkStatus"]);
  },
  useReadQuery(orig_useReadQuery) {
    return wrap(orig_useReadQuery, ["data", "networkStatus"]);
  }
};
function wrap(useFn, transportKeys) {
  return (...args) => {
    const result = useFn(...args);
    const transported = {};
    for (const key of transportKeys) {
      transported[key] = result[key];
    }
    return { ...result, ...useTransportValue(transported) };
  };
}

// src/bundleInfo.ts
var bundle = {
  pkg: "@apollo/client-react-streaming",
  client: "ApolloClient",
  cache: "InMemoryCache"
};

// src/DataTransportAbstraction/WrappedApolloClient.tsx
function getQueryManager(client) {
  return client["queryManager"];
}
var wrappers = Symbol.for("apollo.hook.wrappers");
var ApolloClientBase = class extends ApolloClient$1 {
  /**
   * Information about the current package and it's export names, for use in error messages.
   *
   * @internal
   */
  static info = bundle;
  constructor(options) {
    super(
      options
    );
    if (!(this.cache instanceof InMemoryCache)) {
      throw new Error(
        `When using \`InMemoryCache\` in streaming SSR, you must use the \`${this.constructor.info.cache}\` export provided by \`"${this.constructor.info.pkg}"\`.`
      );
    }
  }
};
var ApolloClientBrowserImpl = class extends ApolloClientBase {
  constructor(options) {
    super(options);
    getQueryManager(this)[wrappers] = hookWrappers;
  }
  simulatedStreamingQueries = /* @__PURE__ */ new Map();
  transportedQueryOptions = /* @__PURE__ */ new Map();
  identifyUniqueQuery(options) {
    const transformedDocument = this.documentTransform.transformDocument(
      options.query
    );
    const queryManager = getQueryManager(this);
    const { serverQuery } = queryManager.getDocumentInfo(transformedDocument);
    if (!serverQuery) {
      throw new Error("could not identify unique query");
    }
    const canonicalVariables = canonicalStringify(options.variables || {});
    const cacheKey = [print(serverQuery), canonicalVariables].toString();
    return { query: serverQuery, cacheKey, varJson: canonicalVariables };
  }
  onQueryStarted = ({
    options,
    id
  }) => {
    const { query, varJson, cacheKey } = this.identifyUniqueQuery(options);
    this.transportedQueryOptions.set(id, options);
    if (!query)
      return;
    const printedServerQuery = print(query);
    const queryManager = getQueryManager(this);
    if (!queryManager["inFlightLinkObservables"].peek(printedServerQuery, varJson)?.observable) {
      let simulatedStreamingQuery, fetchCancelFn;
      const cleanup = () => {
        if (queryManager["fetchCancelFns"].get(cacheKey) === fetchCancelFn)
          queryManager["fetchCancelFns"].delete(cacheKey);
        queryManager["inFlightLinkObservables"].remove(
          printedServerQuery,
          varJson
        );
        if (this.simulatedStreamingQueries.get(id) === simulatedStreamingQuery)
          this.simulatedStreamingQueries.delete(id);
      };
      const promise = new Promise((resolve, reject) => {
        this.simulatedStreamingQueries.set(
          id,
          simulatedStreamingQuery = { resolve, reject, options }
        );
      });
      promise.finally(cleanup);
      const observable = new Observable$1((observer) => {
        promise.then((result) => {
          observer.next(result);
          observer.complete();
        }).catch((err) => {
          observer.error(err);
        });
      });
      queryManager["inFlightLinkObservables"].lookup(
        printedServerQuery,
        varJson
      ).observable = observable;
      queryManager["fetchCancelFns"].set(
        cacheKey,
        fetchCancelFn = (reason) => {
          const { reject } = this.simulatedStreamingQueries.get(id) ?? {};
          if (reject) {
            reject(reason);
          }
          cleanup();
        }
      );
    }
  };
  onQueryProgress = (event) => {
    const queryInfo = this.simulatedStreamingQueries.get(event.id);
    if (event.type === "data") {
      queryInfo?.resolve?.({
        data: event.result.data
      });
      const options = this.transportedQueryOptions.get(event.id);
      if (options) {
        this.cache.writeQuery({
          query: options.query,
          data: event.result.data,
          variables: options.variables
        });
      }
    } else if (event.type === "error") {
      if (queryInfo) {
        this.simulatedStreamingQueries.delete(event.id);
        invariant.debug(
          "query failed on server, rerunning in browser:",
          queryInfo.options
        );
        this.rerunSimulatedQuery(queryInfo);
      }
      this.transportedQueryOptions.delete(event.id);
    } else if (event.type === "complete") {
      this.transportedQueryOptions.delete(event.id);
    }
  };
  /**
   * Can be called when the stream closed unexpectedly while there might still be unresolved
   * simulated server-side queries going on.
   * Those queries will be cancelled and then re-run in the browser.
   */
  rerunSimulatedQueries = () => {
    for (const [id, queryInfo] of this.simulatedStreamingQueries) {
      this.simulatedStreamingQueries.delete(id);
      invariant.debug(
        "streaming connection closed before server query could be fully transported, rerunning:",
        queryInfo.options
      );
      this.rerunSimulatedQuery(queryInfo);
    }
  };
  rerunSimulatedQuery = (queryInfo) => {
    const queryManager = getQueryManager(this);
    const queryId = queryManager.generateQueryId();
    queryManager.fetchQuery(queryId, {
      ...queryInfo.options,
      query: queryManager.transform(queryInfo.options.query),
      context: {
        ...queryInfo.options.context,
        queryDeduplication: false
      }
    }).finally(() => queryManager.stopQuery(queryId)).then(queryInfo.resolve, queryInfo.reject);
  };
};
var ApolloClientImplementation = ApolloClientBrowserImpl ;
var ApolloClient = class extends ApolloClientImplementation {
};

// src/DataTransportAbstraction/symbols.ts
var ApolloClientSingleton = /* @__PURE__ */ Symbol.for(
  "ApolloClientSingleton"
);

// src/DataTransportAbstraction/testHelpers.ts
function resetApolloSingletons() {
  delete window[ApolloClientSingleton];
}
function WrapApolloProvider(TransportProvider) {
  const WrappedApolloProvider3 = ({
    makeClient,
    children,
    ...extraProps
  }) => {
    const clientRef = useRef();
    {
      clientRef.current = window[ApolloClientSingleton] ??= makeClient();
    }
    if (!(clientRef.current instanceof ApolloClient)) {
      throw new Error(
        `When using \`ApolloClient\` in streaming SSR, you must use the \`${WrappedApolloProvider3.info.client}\` export provided by \`"${WrappedApolloProvider3.info.pkg}"\`.`
      );
    }
    return /* @__PURE__ */ React.createElement(ApolloProvider, { client: clientRef.current }, /* @__PURE__ */ React.createElement(
      TransportProvider,
      {
        onQueryEvent: (event) => event.type === "started" ? clientRef.current.onQueryStarted(event) : clientRef.current.onQueryProgress(event),
        rerunSimulatedQueries: clientRef.current.rerunSimulatedQueries,
        registerDispatchRequestStarted: clientRef.current.watchQueryQueue?.register,
        ...extraProps
      },
      children
    ));
  };
  WrappedApolloProvider3.info = bundle;
  return WrappedApolloProvider3;
}
const built_for_browser = true;

export { ApolloClient, DataTransportContext, AccumulateMultipartResponsesLink as DebounceMultipartResponsesLink, InMemoryCache, RemoveMultipartDirectivesLink, SSRMultipartLink, WrapApolloProvider, built_for_browser, resetApolloSingletons };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.browser.js.map